import React from 'react';
import { GlobalStyles as MuiGlobalStyles } from '@mui/material';
import { darken } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { LOGOS } from 'shared/constants';
import variables from 'shared/styleVariables';

const logoBackground = (color: string) => `url('${LOGOS.grey}')
        no-repeat calc(100% + 5rem) calc(100% + 15rem),
        ${color}`;

const getStyles = (theme: Theme) => ({
  /**
   * Avatars
   */
  '.avatar--shadow': {
    boxShadow: theme.shadows[5],
  },
  'a.MuiAvatarGroup-avatar': {
    borderRadius: '50%',
    transition: theme.transitions.create(['opacity', 'max-height'], {
      duration: theme.transitions.duration.short,
    }),
    '& .MuiAvatar-root': {
      fontSize: '1rem',
    },
    '&:hover': {
      transform: 'translateY(-0.2rem)',
      zIndex: '99 !important',
    },
  },
  /**
   * Buttons
   */
  '.button': {
    background: 'none',
    border: 'none',
    color: theme.palette.primary.main,
    outline: 'none',
    padding: 0,
    margin: 0,
    textTransform: 'uppercase',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  '.button--blue': {
    backgroundColor: theme.palette.primary2,
    '&:hover': {
      backgroundColor: darken(theme.palette.primary2, 0.2),
    },
  },
  '.button--small': {
    fontSize: '0.875rem',
  },
  '.button--inline': {
    display: 'inline',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    textDecoration: 'inherit',
    textTransform: 'inherit',
  },
  /**
   * Colors
   */
  html: {
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
  },
  '.background--primary': {
    backgroundColor: variables.backgroundPrimary,
  },
  '.background--secondary': {
    backgroundColor: variables.backgroundSecondary,
  },
  /**
   * Content area layout
   * Consists of two areas: sub and main.
   * Below sm (600px), the areas stack vertically.
   * Above sm, the areas run side by side.
   */
  '.content': {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      height: `calc(100vh - ${variables.headerHeight})`,
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '.content__main': {
    columnGap: '1rem',
    display: 'grid',
    gridTemplateColumns: `
      [edge-start]
      0
      [start]
      repeat(3, 1fr)
      [end]
      0
      [edge-end]`,
    gridTemplateRows: 'max-content',
    overflowY: 'auto',
    padding: '2rem 0 4rem',
    rowGap: '1rem',
    '& > [role="tabpanel"]': {
      columnGap: '1rem',
      display: 'grid',
      gridTemplateColumns: `
        [start]
        repeat(3, 1fr)
        [end]`,
      gridTemplateRows: 'auto',
      rowGap: '1rem',
    },
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'flex-start',
      flexGrow: 1,
      gridTemplateColumns: `
          [edge-start]
          0
          [start]
          repeat(3, 1fr)
          [middle]
          repeat(3, 1fr)
          [end]
          0
          [edge-end]
          `,
      height: `calc(100vh - ${variables.headerHeight})`,
      padding: '1rem 0',
      '& > [role="tabpanel"]': {
        gridTemplateColumns: `
          [start]
          repeat(3, 1fr)
          [middle]
          repeat(3, 1fr)
          [end]`,
      },
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: `
          [edge-start]
          0.5rem
          [start]
          repeat(6, 1fr)
          [middle]
          repeat(6, 1fr)
          [end]
          1rem
          [edge-end]
          `,
      padding: '2rem 0',
      '& > [role="tabpanel"]': {
        gridTemplateColumns: `
          [start]
          repeat(12, 1fr)
          [end]`,
      },
    },
  },
  '.content__main--with-tabs': {
    gridTemplateRows: 'auto auto auto minmax(min-content, 1fr) auto',
  },
  /**
   * Content area styling
   */
  '.content__main--logo': {
    background: logoBackground(theme.palette.common.white),
    backgroundSize: '30rem 30rem',
    '&.background-primary': {
      background: logoBackground(theme.palette.backgroundPrimary),
    },
    '&.background-secondary': {
      background: logoBackground(theme.palette.backgroundSecondary),
    },
  },
  /**
   * Layout
   */
  '.page-grid': {
    display: 'grid',
    gridTemplateColumns: `[page-start nav-start header-start content-start]
      100%
      [content-end header-end nav-end page-end]`,
    gridTemplateRows: `[page-start header-start]
      auto
      [header-end content-start]
      1fr
      [content-end nav-start nav-end page-end]`,
    minHeight: '100vh',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: `[page-start nav-start]
          auto
          [nav-end content-start header-start]
          1fr
          [page-end content-end header-end]`,
      gridTemplateRows: `[page-start header-start]
          ${variables.headerHeight}
          [header-end content-start nav-start ]
          1fr
          [content-end nav-end page-end]`,
      height: '100vh',
    },
  },
  '.page-grid__main': {
    gridColumn: 'content-start / content-end',
    gridRow: 'content-start / content-end',
  },
  '.page-grid__full': {
    gridColumn: 'page-start / page-end',
    gridRow: 'page-start / page-end',
  },
  /**
   * Scroll containers
   */
  '.horizontal-scroll-container': {
    columnGap: '1rem',
    display: 'grid',
    gridAutoFlow: 'row',
    margin: '-0.5rem 0',
    overflowX: 'auto',
    padding: '0.5rem 0 1.5rem 0.25rem',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '.horizontal-scroll-container--main-content': {
    columnGap: '1rem',
    gridColumn: 'edge-start / edge-end',
    display: 'flex',
    height: 'min-content',
    overflowX: 'hidden',
    paddingBottom: '2rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    msOverflowStyle: 'none',
    '&:hover': {
      overflowX: 'auto',
    },
    [theme.breakpoints.up('lg')]: {
      columnGap: '1.5rem',
      paddingLeft: '1.5rem',
      paddingRight: '2rem',
    },
  },
  /**
   * Contacts tables
   */
  '.contactsTable': {
    marginTop: '1rem',
    maxWidth: '100%',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '& .sticky': {
      background: variables.backgroundPrimary,
    },
    '& th': {
      textTransform: 'uppercase',
    },
    '& td:first-of-type, & th:first-of-type, & .MuiCardHeader-root': {
      paddingLeft: 0,
    },
    '& .avatar--shadow': {
      marginLeft: '0.5rem',
    },
    '& a .MuiCardHeader-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInput-root': {
      width: '100%',
    },
  },
  /**
   * MUI overrides
   */
  '.MuiAutocomplete-groupLabel': {
    color: theme.palette.primary1,
  },
  '.MuiInputLabel-formControl': {
    [theme.breakpoints.down('md')]: {
      fontSize: '0.8rem',
    },
  },
  '.MuiPickersLayout-contentWrapper .MuiDateCalendar-root, .MuiPickersLayout-contentWrapper .MuiMonthCalendar-root, .MuiPickersLayout-contentWrapper .MuiYearCalendar-root':
    {
      [theme.breakpoints.down('md')]: {
        minWidth: 'unset',
        width: '100%',
      },
    },
  '.MuiAutocomplete-listbox li:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '.MuiFormHelperText-root': {
    color: variables.primary4,
    margin: 0,
    fontSize: '0.75rem',
    marginTop: '0.1875rem',
    textAlign: 'left',
    fontFamily: variables.sansSerif,
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '0.03333em',
    '&.Mui-error': {
      color: variables.error,
    },
  },
  '.MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
    paddingBottom: 0,
  },
  '.MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-of-type':
    {
      padding: '0.75rem 0 !important',
    },
  '.autocomplete--chips .MuiAutocomplete-inputRoot[class*="MuiInput-root"] .MuiAutocomplete-input:first-of-type':
    {
      padding: '0',
    },
  '.MuiPickersDay-root.MuiPickersDay-selected': {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  '.MuiPickersDay-root.MuiPickersDay-today:not(.MuiPickersDay-selected)': {
    fontWeight: theme.typography.fontWeightBold,
  },
  /**
   * Utilities
   */
  a: {
    color: theme.palette.primary1,
    textDecoration: 'none',
    '&:hover .MuiChip-root': {
      cursor: 'pointer',
    },
  },
  'a[href^="tel:"]': {
    color: 'inherit',
  },
  figure: {
    margin: 0,
  },
  '.error-text': {
    color: variables.error,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 'normal',
    marginBottom: '0.5rem',
  },
  '.nobr': {
    whiteSpace: 'nowrap',
  },
  '.no-scrollbar': {
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '.no-shadow': {
    boxShadow: 'none',
  },
  '.sticky': {
    position: 'sticky',
    left: 0,
    background: 'white',
    zIndex: 100,
  },
  '.truncate': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&.MuiChip-root > span': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  '.visually-hidden': {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  '.userback-button.userback-button-e': {
    marginRight: '15px !important',
  },
  '& .MuiTableContainer-root:not(.noBorder)': {
    boxShadow: 'none',
    border: '1px solid rgba(0,0,0,0.2)',
  },
  '& .MuiFormLabel-root.Mui-error': {
    color: '#5b5b63',
  },
  '& .MuiAutocomplete-root .MuiChip-root': {
    margin: variables.marginChip,
  },
});

const GlobalStyles: React.FC = () => (
  <MuiGlobalStyles styles={getStyles} />
);

export default GlobalStyles;
